// Central content source for the About page.
// Structured so a future website admin area can manage every field
// without touching page components.

import { getLive } from '@/lib/liveContent';

export const ABOUT_SEO = {
  title: 'About Amden Group | Practical Business Consultancy',
  description:
    'Learn how Amden Group helps organisations improve operations, strengthen their people, drive growth and build scalable business systems.',
};

export const ABOUT_HERO = {
  eyebrow: 'About Us',
  headingA: 'About Amden',
  headingAccent: 'Group',
  body: 'Amden Group is a business support partner helping organisations improve operations, build strong teams and drive growth. We combine consultancy with execution across Operations, People, Growth and Business Launch.',
  serviceLineSummary: ['Operations', 'People', 'Growth', 'Launch'],
  taglineCard: 'Advice that guides. Solutions that deliver.',
  image: {
    src: '/assets/about-office.jpg',
    alt: 'The Amden Group team at work in their office beneath the four brand-division wall discs',
    width: 1366,
    height: 768,
  },
};

export const OUR_STORY = {
  eyebrow: 'Our Story',
  heading: 'From SME support to a scalable business partner',
  paragraphs: [
    'Originally focused on supporting SMEs, Amden has evolved into a scalable business support model that now works with both growing businesses and larger organisations.',
    'Our focus remains the same — delivering practical, hands-on support that makes a real difference. We understand that every business is different, which is why we tailor our approach to your specific needs and challenges.',
    'Today, we’re proud to partner with businesses across Ireland and beyond, helping them build better, stronger, more scalable operations.',
  ],
  highlight: 'Practical, hands-on support that makes a real difference.',
  locationChip: 'Kells, Co. Meath, Ireland',
  image: {
    src: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1080&q=80',
    alt: 'The Amden team working through a business plan with clients',
    width: 1080,
    height: 810,
  },
};

export const FOUNDERS_SECTION = {
  eyebrow: 'Meet the Founders',
  heading: 'The people behind Amden Group',
  description: 'Led by experienced professionals passionate about helping businesses succeed.',
};

export const FOUNDERS = [
  {
    id: 'daniel-mcgivern',
    name: 'Daniel McGivern',
    role: 'Co-Founder & Director',
    photo: {
      src: '/assets/founder-daniel.jpg',
      alt: 'Portrait of Daniel McGivern, Co-Founder & Director of Amden Group',
    },
    summary:
      'With over 15 years of experience supporting businesses through growth, operational change, and strategic development, Daniel leads the operational and commercial direction of Amden Group.',
    fullBio: [
      'His background spans business consultancy, process improvement, recruitment strategy, sales development, systems implementation, and operational restructuring across both SMEs and larger organisations. Daniel has worked closely with businesses to improve efficiency, strengthen internal structures, streamline workflows, and create scalable foundations for long-term growth.',
      'Known for his practical, hands-on approach, Daniel focuses on helping businesses bring greater clarity, alignment, and structure across operations, people, and performance.',
    ],
  },
  {
    id: 'claire-mcgivern',
    name: 'Claire McGivern',
    role: 'Co-Founder & Director',
    photo: {
      src: '/assets/founder-claire.jpg',
      alt: 'Portrait of Claire McGivern, Co-Founder & Director of Amden Group',
    },
    summary:
      'With over 20 years of experience across corporate and SME environments, Claire specialises in HR, people development, training, coaching, and organisational support.',
    fullBio: [
      'Her experience includes employee development, onboarding structures, recruitment support, workplace training, and helping businesses build stronger internal cultures during periods of growth and change. Claire works closely with teams to improve communication, strengthen people processes, and support long-term staff development and retention.',
      'Claire brings a practical and people-focused approach to business support, helping organisations create stronger teams, clearer structures, and more positive working environments.',
    ],
  },
];

/**
 * @typedef {Object} Milestone
 * @property {string} id           — milestoneId in the CMS (cms_timeline)
 * @property {number} month
 * @property {number} year
 * @property {string=} exactDate   — ISO date, only where the exact day is verified
 * @property {string} sortDate     — stored date used for chronological sorting
 * @property {string} displayDate  — e.g. "May 2022"
 * @property {string} title
 * @property {string} description
 * @property {string} category     — Foundation | Growth | People | Expansion | Transformation | Systems
 * @property {string=} icon        — key of TIMELINE_ICONS in components/site/about/Timeline.jsx
 * @property {{src: string, alt: string}=} image — approved historic imagery only
 * @property {string=} link        — optional destination route
 * @property {number} displayOrder — tie-breaker within the same month
 * @property {string=} groupKey    — milestones sharing a group render as one connected event
 *
 * Milestones are managed in the admin area (Admin → Timeline) and served from
 * MongoDB via /api/public/timeline. The array below is the verified bundled
 * fallback, used only if the live fetch fails. Do NOT add unverified dates,
 * client numbers or growth claims — only milestones confirmed by Amden Group.
 */
const m = (id, month, year, displayDate, title, description, category, icon, displayOrder, extra = {}) => ({
  id, month, year, exactDate: null, sortDate: `${year}-${String(month).padStart(2, '0')}-01`,
  displayDate, title, description, category, icon, image: null, link: null,
  displayOrder, featured: false, groupKey: null, status: 'published', ...extra,
});

export const TIMELINE = [
  m('rmb-registered-2022', 5, 2022, 'May 2022', 'Run My Business Registered',
    'Run My Business was officially registered with the Companies Registration Office, marking the beginning of the business journey.',
    'Foundation', 'flag', 1),
  m('first-enterprise-office-2023', 4, 2023, 'April 2023', 'First Enterprise Centre Office',
    'Run My Business moved into the Enterprise Centre in Kells, creating a dedicated base from which to support a growing client portfolio.',
    'Growth', 'building', 2),
  m('rmr-established-2023', 8, 2023, 'August 2023', 'Run My Recruitment Established',
    'Run My Recruitment was established to provide clients with practical recruitment support alongside the wider business consultancy offering.',
    'People', 'users', 3),
  m('german-retail-contract-2023', 8, 2023, 'August 2023', 'Major German Retail Contract',
    'The business secured a significant contract supporting a major German retail brand, representing an important step in its commercial growth.',
    'Growth', 'handshake', 4),
  m('digifey-established-2023', 12, 2023, 'December 2023', 'Digifey Established',
    'Digifey was established to provide branding, websites, digital marketing and growth support to businesses.',
    'Growth', 'trending', 5),
  m('office-fitout-2024', 4, 2024, 'April 2024', 'New Office Fit-Out Completed',
    'The fit-out of Amden’s new Kells office was completed, creating a purpose-built space for the expanding team and service offering.',
    'Expansion', 'hardhat', 6),
  m('office-move-2024', 5, 2024, 'May 2024', 'Moved into the New Office',
    'The team moved into its new Kells office, providing a stronger operational base for the next stage of growth.',
    'Expansion', 'building', 7),
  m('google-maps-project-2024', 6, 2024, 'June 2024', 'Major European Google Maps Project',
    'The business agreed a significant European project involving Google Maps, extending its project delivery across multiple European markets.',
    'Expansion', 'globe', 8),
  m('ireland-uk-expansion-2025', 2, 2025, 'February 2025', 'Expansion Across Ireland and the UK',
    'New contracts were agreed across Ireland and the UK, extending the business’s geographical reach and client base.',
    'Expansion', 'map', 9),
  m('four-businesses-one-2025', 12, 2025, 'December 2025', 'Four Businesses Become One',
    'Run My Business, Run My Recruitment, Digifey and The Kaiser Agency were brought together under one joined-up business structure.',
    'Transformation', 'merge', 10, { groupKey: 'amden-transformation-2025' }),
  m('amden-group-launched-2025', 12, 2025, 'December 2025', 'Amden Group Launched',
    'The combined business was rebranded and launched as Amden Group, bringing operations, people, growth and business-launch support together under one brand.',
    'Transformation', 'sparkles', 11, { groupKey: 'amden-transformation-2025', featured: true }),
  m('new-sectors-2026', 2, 2026, 'February 2026', 'Expansion into New Sectors',
    'Amden expanded its work across healthcare, retail, manufacturing and other sectors, applying its consultancy and systems expertise to a broader range of organisations.',
    'Growth', 'layers', 12),
  m('compass-pulse-2026', 7, 2026, 'July 2026', 'Amden Compass and Amden Pulse Launched',
    'Amden launched Compass and Pulse, expanding its offering with practical platforms for business planning, performance, workforce and operational management.',
    'Systems', 'compass', 13, { link: '/systems/compass' }),
  m('reach-launched-2026', 8, 2026, 'August 2026', 'Amden Reach Launched',
    'Amden Reach was launched to give businesses and field-sales teams clearer control of contacts, pipelines, targets and sales activity.',
    'Systems', 'cpu', 14),
];

export const TIMELINE_SECTION = {
  eyebrow: 'Our Journey',
  heading: 'From one business idea to Amden Group',
  description:
    'What began as a focused business-support service has developed into a joined-up consultancy and systems group supporting organisations across Ireland, the UK and Europe.',
};

// Live CMS milestones first (MongoDB is the source of truth); the verified
// bundled array above is the offline fallback. Always chronological by the
// stored sort date — displayOrder only breaks same-month ties.
export const getTimeline = () => {
  const items = getLive('timeline') || TIMELINE;
  return items
    .filter((it) => (it.status || 'published') === 'published')
    .slice()
    .sort(
      (a, b) =>
        String(a.sortDate || '9999').localeCompare(String(b.sortDate || '9999')) ||
        (a.displayOrder ?? 999) - (b.displayOrder ?? 999)
    )
    .map((it) => ({ ...it, id: it.milestoneId || it.id }));
};

export const WHAT_WE_DO = {
  eyebrow: 'What We Do',
  heading: 'What We Do',
  intro: 'We combine consultancy with execution across four key areas.',
  statementA: 'We don’t just advise —',
  statementAccent: 'we deliver.',
  items: [
    {
      id: 'operations',
      name: 'Operations',
      tagline: 'Systems, processes, efficiency',
      color: '#FFB800',
      icon: 'cog',
      href: '/services/operations',
    },
    {
      id: 'people',
      name: 'People',
      tagline: 'HR, recruitment, team building',
      color: '#D6318B',
      icon: 'users',
      href: '/services/people',
    },
    {
      id: 'growth',
      name: 'Growth',
      tagline: 'Marketing, strategy, revenue',
      color: '#029E88',
      icon: 'target',
      href: '/services/growth',
    },
    {
      id: 'launch',
      name: 'Launch',
      tagline: 'New business support',
      color: '#4225C4',
      icon: 'rocket',
      href: '/services/launch',
    },
  ],
};

export const APPROACH = {
  eyebrow: 'Our Approach',
  heading: 'Our Approach',
  intro:
    'We embed ourselves into your business, understand your challenges, and build solutions that are structured, scalable, and commercially focused.',
  principles: [
    {
      icon: 'hammer',
      title: 'Practical Execution',
      text: 'We don’t just advise — we deliver. Our focus is on implementing solutions that work in the real world.',
    },
    {
      icon: 'handshake',
      title: 'Partnership Approach',
      text: 'We embed ourselves into your business, understanding your challenges and working alongside your team.',
    },
    {
      icon: 'layers',
      title: 'Scalable Solutions',
      text: 'Every solution we design is built for growth, ensuring your systems evolve with your business.',
    },
  ],
  promise: {
    eyebrow: 'Our Promise',
    points: [
      'Practical, implementable solutions',
      'Hands-on support throughout',
      'Measurable outcomes',
      'Long-term partnership',
    ],
  },
};

export const ABOUT_CTA = {
  eyebrow: 'Next step',
  heading: 'Ready to build a stronger business?',
  copy: 'Let’s discuss where your business is now, where you want it to go and the practical support needed to get there.',
  primaryLabel: 'Get Started',
  secondaryLabel: 'Explore Our Services',
  secondaryHref: '/services',
};
